import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Button, Card, Divider, Drawer, Flex, Tag, Typography, useMessagedAsyncRetry } from "@payping/web-components";
import { type SDKRequestInit } from "@payping/api";
import { bnplConsumerGetPlanList } from "@payping/redux/actions";
import { APP_ROUTES, returnToLastUrl, useAppDispatch, useAppSelector, useAppTranslation } from "@/utils";
import UnAvailable from "@/pages/Pay/ChoosePlan/UnAvailable";
import { Close, CuToman, FiArrowRight, PhRStorefront } from "@payping/icons";
import Loading from "@/pages/loading";
import { GuaranteeType } from "@payping/api/app/api/bnplConsumerTypes";
import type { PlanInfo } from "@payping/api/app/api/bnplConsumerTypes";
import { dashboardPersistReduxActions } from "@/redux/actions/dashboardPersist";
import "./index.module.scss";
import { clsx } from "clsx";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import PayUnAvailable from "../Unavailable";
import PayExpiredOrder from "../ExpiredOrder";
import SelectedPlan from "./selectedPlan";

const PayChoosePlan = () => {
  const { t } = useAppTranslation();
  const [selectedPlan, setSelectedPlan] = useState<PlanInfo | undefined>({});
  const [showSelectedPlan, setShowSelectedPlan] = useState(false);
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const trackingCode = searchParams.get("trackingCode") || "";
  const planList = useAppSelector((state) => state.bnplConsumer.planList);
  const navigate = useNavigate();
  const trackingCodesSetPlanedOrders = useAppSelector((state) => state.dashboardPersist.setPlanedTrackingCodes);

  useEffect(() => {
    if (!trackingCode) {
      navigate(APP_ROUTES.home.overview.url);
    } else {
      dispatch(dashboardPersistReduxActions.setTrackingCode(trackingCode));
    }
  }, [dispatch, navigate, trackingCode]);

  const {
    loading: getPlanListLoading,
    error,
    retry,
    value: getPlanList,
  } = useMessagedAsyncRetry(
    async () => {
      try {
        const res = await dispatch(bnplConsumerGetPlanList(trackingCode, { isRaw: true } as SDKRequestInit));
        if (res.merchantName && res.merchantRedirectUrl) {
          dispatch(dashboardPersistReduxActions.setShopInfo(res.merchantName, res.merchantRedirectUrl));
        }

        return res;
      } catch (e: any) {
        // Check if e is an instance of Response and if the status is 400
        if (e.status === 400) {
          try {
            const parsedError = await e.json();
            throw parsedError;
          } catch (jsonError: any) {
            if (jsonError.status === 400) {
              throw jsonError;
            }
            throw e; // Fallback if parsing JSON fails
          }
        }
        // Handle any other errors
        throw e;
      }
    },
    [dispatch, trackingCode],
    {
      success: false,
      error: true,
    },
  );

  useEffect(() => {
    if (trackingCodesSetPlanedOrders.includes(trackingCode)) {
      navigate(`${APP_ROUTES.pay.getOrder.root.url}?trackingCode=${trackingCode}`);
    }
  }, [navigate, trackingCode, trackingCodesSetPlanedOrders]);

  const handleShowGuaranteeType = (type: GuaranteeType) => {
    if (type === GuaranteeType.Cheque) return t("common.guaranteeTypes.cheque");
    if (type === GuaranteeType.None) return t("common.guaranteeTypes.none");
    if (type === GuaranteeType.Salary) return t("common.guaranteeTypes.salary");

    return t("common.guaranteeTypes.promissory");
  };

  const handleClickChoosePlan = () => {
    navigate(`${APP_ROUTES.pay.setPlan.url}?trackingCode=${trackingCode}&planId=${selectedPlan?.planId}`);
  };

  // expired order
  if ((error as any)?.key === 109) {
    return (
      <PayExpiredOrder
        backToShop={() => {
          const url = planList.merchantRedirectUrl;
          if (!url) return;
          window.location.replace(url);
        }}
        shopName={planList.merchantName ? planList.merchantName : undefined}
      />
    );
  }

  if (error) {
    return (
      <PayUnAvailable
        onRetry={() => {
          retry();
        }}
        onRetryLoading={getPlanListLoading}
        shopName={t("common.shop")}
        backToShop={() => {
          window.history.back();
        }}
      />
    );
  }

  // no active plan
  if (getPlanList && planList.plansInfo?.length === 0) {
    return <UnAvailable name={planList.merchantName} url={planList.merchantRedirectUrl} />;
  }

  if (getPlanListLoading) {
    return <Loading />;
  }

  return (
    <div
      className={clsx([
        "tw:relative tw:h-full",
        {
          "tw:overflow-hidden": showSelectedPlan,
        },
      ])}
    >
      <Flex vertical className="tw:h-full">
        <Flex
          onClick={() => {
            returnToLastUrl();
          }}
          align="center"
          className="tw:h-16 tw:min-h-16 tw:cursor-pointer tw:justify-start tw:px-4!"
        >
          <FiArrowRight width={16} height={16} />
          <Typography.Text className="tw:mx-1!">{t("common.returnToShop")}</Typography.Text>
        </Flex>
        <Divider className="tw:m-0!" />
        <Flex vertical>
          <Flex vertical className="tw:border-0 tw:border-b-4 tw:border-solid tw:border-color-border-secondary tw:p-4!">
            <Flex align="center" gap={"small"}>
              <Card size="small" classNames={{ body: "tw:p-2! tw:flex tw:items-center" }}>
                <PhRStorefront width={24} className="tw:p-0! tw:text-color-text-tertiary" />
              </Card>
              <Typography.Text>{planList.merchantName}</Typography.Text>
            </Flex>
            <Flex className="tw:mt-7!" align="center" justify="space-between">
              <Typography.Text type="secondary">{t("pay.totalCheckoutAmount")}</Typography.Text>
              <Flex>
                <Typography.Text>{formatLocalizedNumber({ value: planList.basketTotalAmount ?? 0 })}</Typography.Text>
                <CuToman width={24} height={24} />
              </Flex>
            </Flex>
          </Flex>

          <Flex vertical className="tw:p-4!">
            <Typography.Title level={5} className="tw:mt-12! tw:text-lg">
              {t("pay.availablePlan")}
            </Typography.Title>
            <Flex gap={"large"} vertical className="tw:mt-6!">
              {planList.plansInfo?.map((item, index) => (
                <Flex
                  key={item.planId ?? index}
                  vertical
                  onClick={() => {
                    setSelectedPlan({ ...item });
                    setShowSelectedPlan(true);
                  }}
                  className={clsx([
                    "tw:h-34.25 tw:w-full tw:rounded-md tw:border tw:border-solid tw:p-3!",
                    {
                      "tw:border-color-primary": selectedPlan?.planId === item.planId,
                      "tw:border-color-border-secondary": selectedPlan?.planId !== item.planId,
                    },
                  ])}
                >
                  <Flex align="flex-start" justify="space-between" className="tw:w-full">
                    <Flex>
                      <Typography.Text className="tw:text-xl! tw:font-semibold!">
                        {formatLocalizedNumber({ value: item.eachInstallmentPayment ?? 0 })}
                      </Typography.Text>
                      <CuToman width={24} height={24} />
                      <Typography.Text className="tw:text-md!">/ {t("common.monthly")} </Typography.Text>
                    </Flex>
                    <Tag>{handleShowGuaranteeType(item.guaranteeType ?? 0)}</Tag>
                  </Flex>
                  <Typography.Text type="secondary" className="tw:mt-2! tw:text-sm!">
                    {item.installmentsCount} {t("common.month")}
                  </Typography.Text>
                  <Divider className="tw:mb-0! tw:mt-4!" />
                  <Flex align="flex-start" justify="space-between" className="tw:mt-4! tw:w-full">
                    <Typography.Text type="secondary" className="tw:text-md!">
                      {t("pay.prePayment")}
                    </Typography.Text>
                    <Flex>
                      <Typography.Text className="tw:text-md!">
                        {formatLocalizedNumber({ value: item.prepayment ?? 0 })}
                      </Typography.Text>
                      <CuToman width={24} height={24} />
                    </Flex>
                  </Flex>
                </Flex>
              ))}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      <Drawer
        open={showSelectedPlan}
        title={
          <Flex align="center" gap={"small"}>
            <Button icon={<Close width={14} height={14} />} type="text" onClick={() => setShowSelectedPlan(false)} />
            <Typography.Title className="tw:m-0! tw:p-0!" level={5}>
              {t("pay.selectedPlanInfo")}
            </Typography.Title>
          </Flex>
        }
        onClose={() => setShowSelectedPlan(false)}
        size={"90%"}
        placement={"bottom"}
        closeIcon={false}
        classNames={{ body: "tw:p-4!" }}
        getContainer={false}
      >
        <SelectedPlan plan={selectedPlan} onConfirm={handleClickChoosePlan} />
      </Drawer>
    </div>
  );
};
export default PayChoosePlan;
